'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { InfoCard } from '@/ui/cards/info-card/InfoCard'
import { FilterGroup } from '@/ui/form/inputs/filter-group/FilterGroup.client'
import { useAtom } from 'jotai'
import { startTransition, useCallback, type RefObject } from 'react'
import { useDebounceCallback } from 'usehooks-ts'
import {
    selectedReferenceAtom,
    showMonoDiscriminantInfoCardAtom,
} from '../../services/product.store'
import type { MergedProduct } from '../../types/mergedProduct'

const CHANGE_ARTICLE_ACTIONS_DEBOUNCE_MS = 300

type ProductBuyBoxMonoDiscriminantFiltersProps = {
  filters: MergedProduct['filters']
  ref: RefObject<HTMLDivElement | null>
}

export function ProductBuyBoxMonoDiscriminantFilters({
  filters,
  ref,
}: ProductBuyBoxMonoDiscriminantFiltersProps) {
  const { t } = useTranslation(['productBuyBox'])
  const [selectedReference, setSelectedReference] = useAtom(selectedReferenceAtom)
  const [showMonoDiscriminantInfoCard, setShowMonoDiscriminantInfoCard] = useAtom(
    showMonoDiscriminantInfoCardAtom,
  )

  /**
   * Handle actions when changing the selected article.
   * It is used to manage actions that must be debounced and not executed on each selection change.
   */
  const handleChangeArticleActions = useCallback((reference: string, eventLabel: string) => {
    const url = new URL(window.location.href)
    url.searchParams.set('reference', reference)
    url.searchParams.set('type', 'product')

    handleIntent(INTENT_SAME_AS_WEB, {
      whenInBrowser: () => {
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'fiche produit mono-discriminant',
          eventAction: 'bloc prix - sélection discriminant',
          eventLabel,
        })
      },
    })
    window.history.replaceState(null, '', url)
  }, [])

  const handleChangeArticleActionsDebounced = useDebounceCallback(
    handleChangeArticleActions,
    CHANGE_ARTICLE_ACTIONS_DEBOUNCE_MS,
  )

  if (!filters || filters.length === 0 || !filters[0] || !filters[0].values) {
    return null
  }

  return (
    <div ref={ref}>
      <FilterGroup
        filter={{
          label: filters[0].label,
          values: filters[0].values.map(({ label, references }) => ({
            label,
            value: references?.[0] ?? '',
          })),
        }}
        onSelectionChange={(_label, filter) => {
          if (!filter) {
            return
          }

          const reference = filter as string
          const selectedValue =
            filters?.[0]?.values?.find((v) => v.references?.[0] === reference)?.label || reference

          // Avoids React tearing down the whole product details Suspense boundary while downstream consumers re-render.
          startTransition(() => {
            setShowMonoDiscriminantInfoCard(false)
            setSelectedReference(reference)
          })
          handleChangeArticleActionsDebounced(reference, `${filters[0]?.label} | ${selectedValue}`)
        }}
        selectedValues={selectedReference ?? undefined}
        className="mt-0"
      />
      {showMonoDiscriminantInfoCard && (
        <InfoCard
          variant="warning"
          className="mt-2"
          title={t('productBuyBox:errors.monoDiscriminant.selectArticle')}
        />
      )}
    </div>
  )
}
